// Header pill showing the active workspace + a dropdown to switch. The
// switcher stores the selection in localStorage and reloads the page so every
// in-flight query picks up the new X-Workspace-Id (the fetch client reads it
// from the stored key — see lib/workspace.ts).
//
// Pre-Clerk the internal-team caller only ever sees the `default` workspace.
// This component still renders (no dropdown) to give the layout a stable slot
// once Clerk lands and real workspaces arrive.

import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { setActiveWorkspaceId } from '@/lib/workspace';
import { cn } from '@/lib/cn';

const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  solo_pro: 'Solo Pro',
  team: 'Team',
  enterprise: 'Enterprise',
};

export function WorkspaceSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['workspaces-switcher'],
    queryFn: api.listWorkspaces,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (isLoading || !data) {
    return (
      <div className="hidden items-center gap-2 rounded-lg border border-rule bg-surface-sunk px-3 py-1.5 text-[0.8125rem] text-ink-muted sm:flex">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Workspace</span>
      </div>
    );
  }

  const active = data.workspaces.find((w) => w.id === data.active_workspace_id);
  const multiple = data.workspaces.length > 1;

  const onPick = (id: string) => {
    setActiveWorkspaceId(id);
    setOpen(false);
    // Simplest consistent invalidation: reload. The typical alternative is to
    // invalidate the entire react-query cache — reload is honest about the
    // fact that every tenant-scoped view needs to requery.
    window.location.reload();
  };

  return (
    <div ref={ref} className="relative hidden sm:block" data-testid="workspace-switcher">
      <button
        type="button"
        onClick={() => multiple && setOpen((o) => !o)}
        disabled={!multiple}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-rule bg-surface-sunk px-3 py-1.5 text-left',
          multiple
            ? 'hover:border-rule-strong hover:bg-surface'
            : 'cursor-default opacity-90'
        )}
      >
        <div>
          <div className="text-[0.8125rem] font-semibold leading-tight text-ink">
            {active?.name ?? 'Workspace'}
          </div>
          <div className="text-[0.6875rem] leading-tight text-ink-muted">
            {active ? PLAN_LABEL[active.plan] ?? active.plan : ''}
            {active?.billing_exempt ? ' · internal' : ''}
          </div>
        </div>
        {multiple && <ChevronDown className="h-3.5 w-3.5 text-ink-muted" />}
      </button>
      {open && multiple && (
        <div className="absolute right-0 top-full z-30 mt-2 min-w-[240px] rounded-lg border border-rule bg-surface p-1 shadow-lg">
          {data.workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => onPick(w.id)}
              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-[0.8125rem] hover:bg-surface-sunk"
            >
              <div className="min-w-0">
                <div className="truncate font-semibold text-ink">{w.name}</div>
                <div className="text-[0.6875rem] text-ink-muted">
                  {PLAN_LABEL[w.plan] ?? w.plan} · {w.balance} cr · {w.role}
                </div>
              </div>
              {w.id === data.active_workspace_id && (
                <Check className="h-4 w-4 shrink-0 text-brand-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
